// ─── Simulador Público — embeddable, sin auth ────────────────

import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { Spinner } from '@components/ui/Spinner';
import { fetchSimuladorProyecto } from '@api/portal';
import { formatearMonto } from '@utils/dateFormatters';
import { calcularMensualidadBase, calcularEnganche, redondear2 } from '@erp/shared';

const SimuladorPublico: React.FC = () => {
  const { proyecto_id } = useParams<{ proyecto_id: string }>();
  const proyectoId = Number(proyecto_id);

  const { data: proyecto, isLoading, isError } = useQuery({
    queryKey: ['simulador-publico', proyectoId],
    queryFn:  () => fetchSimuladorProyecto(proyectoId),
    enabled:  !isNaN(proyectoId) && proyectoId > 0,
    staleTime: 60_000,
  });

  const [enganchePct, setEnganchePct] = useState<number | null>(null);
  const [plazoMeses, setPlazoMeses] = useState<number | null>(null);

  const pctEfectivo = enganchePct ?? proyecto?.enganche_min_pct ?? 20;
  const plazoEfectivo = plazoMeses ?? proyecto?.meses_opciones?.[0] ?? 12;

  const calculo = useMemo(() => {
    if (!proyecto) return null;
    const precio = proyecto.precio_base;
    // Tarea 5 de I-128: el mismo defecto que la mensualidad — sin redondear, mientras
    // backend sí redondea el enganche (crm.service.ts:2810, 4251, 4446).
    const engancheAmt = calcularEnganche(precio, pctEfectivo);
    const saldo = redondear2(precio - engancheAmt);

    // BUG-1671 (I-128, 2026-09-15): este simulador calculaba la mensualidad sin
    // redondear, con su PROPIA álgebra (distinta a la del cotizador embebible y a
    // la del backend), y es tan público como aquél — ruta viva en /simulador/:proyecto_id
    // (App.tsx:266), sin auth. Ahora es LA MISMA cuenta que usa toda la casa:
    // `calcularMensualidadBase` de '@erp/shared'. No reescribas esta fórmula aquí.
    const tasaAnual = proyecto.usa_tasa_interes ? proyecto.tasa_interes_anual : 0;
    const mensualidad = calcularMensualidadBase(saldo, tasaAnual, plazoEfectivo);

    return { precio, engancheAmt, saldo, mensualidad };
  }, [proyecto, pctEfectivo, plazoEfectivo]);

  if (isNaN(proyectoId) || proyectoId <= 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-sm" style={{ color: '#EF4444' }}>ID de proyecto inválido.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" color="blue" />
      </div>
    );
  }

  if (isError || !proyecto) {
    return (
      <div className="flex items-center justify-center min-h-screen text-center px-4">
        <div>
          <p className="text-base font-semibold" style={{ color: '#111827' }}>
            Proyecto no disponible
          </p>
          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
            El cotizador de este proyecto no está disponible en este momento.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#F9FAFB' }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 flex flex-col gap-6"
        style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
      >
        {/* Header */}
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#9CA3AF' }}>
            Cotizador
          </p>
          <p className="text-xl font-bold" style={{ color: '#111827' }}>{proyecto.nombre}</p>
          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
            Precio desde {formatearMonto(proyecto.precio_base)}
          </p>
        </div>

        {/* Enganche slider */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold" style={{ color: '#374151' }}>
              Enganche
            </label>
            <span className="text-sm font-bold" style={{ color: '#2563EB' }}>
              {pctEfectivo}%
              {calculo && (
                <span className="font-normal text-xs ml-1" style={{ color: '#6B7280' }}>
                  ({formatearMonto(calculo.engancheAmt)})
                </span>
              )}
            </span>
          </div>
          <input
            type="range"
            min={proyecto.enganche_min_pct}
            max={proyecto.enganche_max_pct}
            step={5}
            value={pctEfectivo}
            onChange={(e) => setEnganchePct(Number(e.target.value))}
            className="w-full accent-blue-600"
          />
          <div className="flex justify-between text-xs" style={{ color: '#9CA3AF' }}>
            <span>{proyecto.enganche_min_pct}%</span>
            <span>{proyecto.enganche_max_pct}%</span>
          </div>
        </div>

        {/* Plazo */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold" style={{ color: '#374151' }}>Plazo</label>
          <div className="flex flex-wrap gap-2">
            {proyecto.meses_opciones.map((m) => (
              <button
                key={m}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                style={
                  m === plazoEfectivo
                    ? { backgroundColor: '#2563EB', color: '#FFFFFF' }
                    : { backgroundColor: '#F3F4F6', color: '#374151' }
                }
                onClick={() => setPlazoMeses(m)}
              >
                {m} meses
              </button>
            ))}
          </div>
        </div>

        {/* Resultado */}
        {calculo && (
          <div
            className="rounded-xl p-4 flex flex-col gap-3"
            style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}
          >
            <div className="text-center">
              <p className="text-xs" style={{ color: '#1E40AF' }}>Mensualidad estimada</p>
              <p className="text-3xl font-bold mt-1" style={{ color: '#1D4ED8' }}>
                {formatearMonto(calculo.mensualidad)}
              </p>
              <p className="text-xs mt-1" style={{ color: '#60A5FA' }}>
                /mes por {plazoEfectivo} meses
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2 border-t" style={{ borderColor: '#BFDBFE' }}>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#1E40AF' }}>Saldo a financiar</p>
                <p className="text-sm font-semibold" style={{ color: '#1D4ED8' }}>
                  {formatearMonto(calculo.saldo)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#1E40AF' }}>Total enganche</p>
                <p className="text-sm font-semibold" style={{ color: '#1D4ED8' }}>
                  {formatearMonto(calculo.engancheAmt)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <p className="text-xs text-center" style={{ color: '#9CA3AF' }}>
          * Valores estimados. El plan de pagos exacto lo confirma tu asesor.
        </p>
        <a
          href={`https://wa.me/?text=Hola, me interesa el proyecto ${encodeURIComponent(proyecto.nombre)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3 rounded-xl text-sm font-semibold text-center text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#22C55E' }}
        >
          Solicitar información por WhatsApp
        </a>

        {/* Footer — Desarrollador & Master Broker */}
        <div className="border-t pt-4 flex justify-between items-start gap-4" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#9CA3AF' }}>Desarrollador</span>
            <span className="text-sm font-semibold" style={{ color: '#111827' }}>RM Desarrollos</span>
            <a
              href="https://rmdesarrollos.com.mx/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs flex items-center gap-1 hover:underline"
              style={{ color: '#3B82F6' }}
            >
              <ExternalLink size={10} />
              rmdesarrollos.com.mx
            </a>
          </div>
          <div className="flex flex-col gap-0.5 text-right">
            <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#9CA3AF' }}>Master Broker</span>
            <span className="text-sm" style={{ color: '#6B7280' }}>—</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimuladorPublico;
